import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Navigation,
  Search,
  Filter,
  ChevronRight,
  Utensils,
  Car,
  Ticket,
  ShieldAlert,
  Droplet,
  Info,
  Package,
  Users,
  Flag,
  X,
  Loader2,
  Coffee,
  MapPinned,
} from 'lucide-react';
import { api } from '../../lib/api';

// Available icons mapping
const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  utensils: Utensils,
  droplet: Droplet,
  car: Car,
  ticket: Ticket,
  shield: ShieldAlert,
  package: Package,
  info: Info,
  users: Users,
  flag: Flag,
  navigation: Navigation,
  coffee: Coffee,
  mappin: MapPin,
};

// POI Categories and Icons
const POI_CATEGORIES = {
  food: { icon: Utensils, label: 'Food & Drinks', color: 'bg-orange-500' },
  restrooms: { icon: Droplet, label: 'Restrooms', color: 'bg-blue-500' },
  parking: { icon: Car, label: 'Parking', color: 'bg-gray-500' },
  tickets: { icon: Ticket, label: 'Tickets', color: 'bg-purple-500' },
  medical: { icon: ShieldAlert, label: 'First Aid', color: 'bg-red-500' },
  merchandise: { icon: Package, label: 'Merchandise', color: 'bg-pink-500' },
  info: { icon: Info, label: 'Information', color: 'bg-green-500' },
  vip: { icon: Users, label: 'VIP Areas', color: 'bg-yellow-500' },
  grandstand: { icon: Flag, label: 'Grandstands', color: 'bg-indigo-500' },
};

type POICategory = keyof typeof POI_CATEGORIES;

interface POI {
  id: string;
  name: string;
  category: POICategory;
  description: string;
  x: number;
  y: number;
  latitude?: number | null;
  longitude?: number | null;
  floor?: string | null;
  iconName?: string | null;
  customIconUrl?: string | null;
  isOpen?: boolean;
  waitTime?: string | null;
}

// Track venue map
const TRACK_MAP_IMAGE = '/map.png';

export function MapPage() {
  const [pois, setPOIs] = useState<POI[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<Set<POICategory>>(new Set());
  const [selectedPOI, setSelectedPOI] = useState<POI | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch POIs from API
  useEffect(() => {
    const fetchPOIs = async () => {
      try {
        const response = await api.get<POI[]>('/api/public/pois', false);
        if (response.success && response.data) {
          setPOIs(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch POIs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPOIs();
  }, []);

  const filteredPOIs = useMemo(() => {
    return pois.filter((poi) => {
      const matchesSearch =
        searchQuery === '' ||
        poi.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (poi.description || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategories.size === 0 || selectedCategories.has(poi.category);
      return matchesSearch && matchesCategory;
    });
  }, [pois, searchQuery, selectedCategories]);

  const toggleCategory = (category: POICategory) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const getIconComponent = (poi: POI) => {
    if (poi.iconName && ICON_MAP[poi.iconName]) {
      return ICON_MAP[poi.iconName];
    }
    return POI_CATEGORIES[poi.category]?.icon || MapPin;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Venue Map</h1>
            <p className="text-gray-600">Find your way around DriveIT NQ</p>
          </div>
          <Link
            to="/navigate"
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors"
          >
            <Navigation size={20} />
            Get Directions
          </Link>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search for locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-red-500 focus:border-red-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-4 py-3 border rounded-xl transition-colors ${
              selectedCategories.size > 0
                ? 'border-red-600 bg-red-50 text-red-600'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter size={20} />
            Filters
            {selectedCategories.size > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-red-600 text-white text-xs rounded-full">
                {selectedCategories.size}
              </span>
            )}
          </button>
        </div>

        {/* Category Filters */}
        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 rounded-xl">
            <div className="flex flex-wrap gap-2">
              {(Object.entries(POI_CATEGORIES) as [POICategory, typeof POI_CATEGORIES.food][]).map(
                ([key, { icon: Icon, label, color }]) => (
                  <button
                    key={key}
                    onClick={() => toggleCategory(key)}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategories.has(key)
                        ? `${color} text-white`
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                )
              )}
              {selectedCategories.size > 0 && (
                <button
                  onClick={() => setSelectedCategories(new Set())}
                  className="px-3 py-2 text-sm text-red-600 hover:text-red-700"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Map View */}
          <div className="lg:col-span-2">
            <div className="relative aspect-[4/3] bg-gray-100 rounded-2xl overflow-hidden">
              {/* Map background */}
              <img
                src={TRACK_MAP_IMAGE}
                alt="DriveIT NQ Track Map"
                className="w-full h-full object-contain bg-white"
              />

              {/* POI Markers */}
              {filteredPOIs.map((poi) => {
                const Icon = getIconComponent(poi);
                const { color } = POI_CATEGORIES[poi.category] || { color: 'bg-gray-500' };
                const isSelected = selectedPOI?.id === poi.id;

                return (
                  <button
                    key={poi.id}
                    onClick={() => setSelectedPOI(isSelected ? null : poi)}
                    className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all ${
                      isSelected ? 'z-20 scale-125' : 'z-10 hover:scale-110'
                    }`}
                    style={{ left: `${poi.x}%`, top: `${poi.y}%` }}
                    title={poi.name}
                  >
                    {poi.customIconUrl ? (
                      <img
                        src={poi.customIconUrl}
                        alt={poi.name}
                        className={`w-8 h-8 rounded-full object-cover border-2 shadow-lg ${
                          isSelected ? 'border-red-600 ring-4 ring-red-200' : 'border-white'
                        }`}
                      />
                    ) : (
                      <div
                        className={`w-8 h-8 ${color} rounded-full flex items-center justify-center text-white shadow-lg ${
                          isSelected ? 'ring-4 ring-white' : ''
                        }`}
                      >
                        <Icon size={16} />
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute top-10 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white px-3 py-1 rounded-lg shadow-lg text-sm font-medium text-gray-900">
                        {poi.name}
                      </div>
                    )}
                  </button>
                );
              })}

              {/* Map legend */}
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur rounded-lg p-3">
                <p className="text-xs font-medium text-gray-700 mb-2">Legend</p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {(Object.entries(POI_CATEGORIES) as [POICategory, typeof POI_CATEGORIES.food][])
                    .slice(0, 6)
                    .map(([key, { icon: Icon, label, color }]) => (
                      <div key={key} className="flex items-center gap-1">
                        <div className={`w-4 h-4 ${color} rounded-full flex items-center justify-center`}>
                          <Icon size={10} className="text-white" />
                        </div>
                        <span className="text-gray-600">{label.split(' ')[0]}</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* No POIs message */}
              {pois.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <div className="bg-white px-6 py-4 rounded-xl shadow-lg text-center">
                    <MapPin size={32} className="mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-600">No points of interest yet</p>
                  </div>
                </div>
              )}
            </div>

            {/* Selected POI Details */}
            {selectedPOI && (
              <div className="mt-4 card p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {selectedPOI.customIconUrl ? (
                      <img
                        src={selectedPOI.customIconUrl}
                        alt={selectedPOI.name}
                        className="w-12 h-12 rounded-xl object-cover"
                      />
                    ) : (
                      <div
                        className={`w-12 h-12 ${
                          POI_CATEGORIES[selectedPOI.category]?.color || 'bg-gray-500'
                        } rounded-xl flex items-center justify-center text-white`}
                      >
                        {(() => {
                          const Icon = getIconComponent(selectedPOI);
                          return <Icon size={24} />;
                        })()}
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">{selectedPOI.name}</h3>
                      <p className="text-gray-600">{selectedPOI.description}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {selectedPOI.floor && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            {selectedPOI.floor}
                          </span>
                        )}
                        {selectedPOI.isOpen !== undefined && (
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              selectedPOI.isOpen
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {selectedPOI.isOpen ? 'Open' : 'Closed'}
                          </span>
                        )}
                        {selectedPOI.waitTime && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                            Wait: {selectedPOI.waitTime}
                          </span>
                        )}
                        {selectedPOI.latitude && selectedPOI.longitude && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                            <MapPinned size={12} className="inline mr-1" />
                            GPS Available
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedPOI(null)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="mt-4 flex gap-3">
                  <Link
                    to={`/navigate?to=${selectedPOI.id}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Navigation size={18} />
                    Get Directions
                  </Link>
                  {selectedPOI.latitude && selectedPOI.longitude && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${selectedPOI.latitude},${selectedPOI.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <MapPinned size={18} />
                      Google Maps
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* POI List */}
          <div className="lg:col-span-1">
            <div className="card p-4">
              <h2 className="font-semibold text-gray-900 mb-4">
                {filteredPOIs.length} Location{filteredPOIs.length !== 1 ? 's' : ''}
              </h2>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredPOIs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No locations found</p>
                ) : (
                  filteredPOIs.map((poi) => {
                    const Icon = getIconComponent(poi);
                    const { color } = POI_CATEGORIES[poi.category] || { color: 'bg-gray-500' };
                    return (
                      <button
                        key={poi.id}
                        onClick={() => setSelectedPOI(poi)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${
                          selectedPOI?.id === poi.id
                            ? 'bg-red-50 border border-red-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {poi.customIconUrl ? (
                          <img
                            src={poi.customIconUrl}
                            alt={poi.name}
                            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div
                            className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center text-white flex-shrink-0`}
                          >
                            <Icon size={18} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{poi.name}</p>
                          <p className="text-sm text-gray-500 truncate">{poi.description}</p>
                        </div>
                        <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Access */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { category: 'restrooms' as POICategory, title: 'Find Restrooms' },
            { category: 'food' as POICategory, title: 'Find Food' },
            { category: 'medical' as POICategory, title: 'First Aid' },
            { category: 'info' as POICategory, title: 'Information' },
          ].map(({ category, title }) => {
            const { icon: Icon, color } = POI_CATEGORIES[category];
            const count = pois.filter((p) => p.category === category).length;
            return (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategories(new Set([category]));
                  setShowFilters(true);
                }}
                className="card p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
              >
                <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center text-white`}>
                  <Icon size={24} />
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900">{title}</p>
                  <p className="text-sm text-gray-500">{count} location{count !== 1 ? 's' : ''}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
